// Auth state + login/logout/me wrappers around the kitchenos-api retailer
// portal procedures. Cookie-based — `kos_portal` is HttpOnly and managed by
// the server (see /var/www/kitchenos-api/server.js around line 663).
//
// Everything here is synchronous-feeling for the chrome: call `getCurrentUser`
// once on boot, then `login` / `logout` mutate the in-memory cache and fire
// listeners so the chrome can re-render.
//
// The retailer-portal session-check endpoint returns only {isPortal: true},
// so we cache the retailerId/retailerName from the login response in
// localStorage and rehydrate from there on boot. The cookie remains the
// source of truth for authentication; localStorage just remembers identity.

import { api, ApiError } from "@tkos/api-client";
import type { AuthUser } from "./types";

// ── Endpoints ────────────────────────────────────────────────────────────────

const LOGIN_PATH = "/retailer/login";
const LOGOUT_PATH = "/portal/demo-logout";
const CHECK_PATH = "/portal/demo-check";
const ME_PATH = "/portal/me";

const SESSION_STORAGE_KEY = "tkos.retailer.session";

// ── Server response shapes ───────────────────────────────────────────────────

interface RetailerLoginResponse {
  ok: true;
  /** /api/retailer/login returns the retailer record; `id` is the slug. */
  retailer: {
    id: string;
    name: string;
    email: string;
    contactName?: string;
    phone?: string;
    createdAt?: string;
  };
  /** The signed-in individual (per-user auth). `id` is the user id, `role` the
   *  permission role. Absent only for very old server builds. */
  user?: { id: string | null; name: string; role: string };
}

interface CheckResponse {
  isPortal: boolean;
}

interface MeResponse {
  ok: true;
  user: { firstName: string; lastName: string; email: string; role: string } | null;
  /** From `retailers.onboarding_complete`. Absent/false → treat as not
   *  onboarded so demo data is never shown to a real account. */
  onboardingComplete?: boolean;
  /** Public-facing slug (e.g. r-h190uc1rtw5c) for wizard /start/ links. */
  retailerSlug?: string;
  /** Authoritative tenant identity from the verified session cookie. The
   *  front end refreshes its cached values from these so a stale localStorage
   *  tenant can never override the signed-in retailer. */
  retailerId?: string;
  retailerName?: string;
}

// ── In-memory auth state ─────────────────────────────────────────────────────

let currentUser: AuthUser | null = null;
const listeners = new Set<(user: AuthUser | null) => void>();

/** Subscribe to auth-state changes. Returns an unsubscribe function. */
export function onAuthChange(fn: (user: AuthUser | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function setCurrentUser(user: AuthUser | null): void {
  currentUser = user;
  for (const fn of listeners) fn(user);
}

/** Synchronous getter for the cached user. Use after `getCurrentUser()`. */
export function getCachedUser(): AuthUser | null {
  return currentUser;
}

// ── localStorage helpers ─────────────────────────────────────────────────────

function saveCachedSession(user: AuthUser): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
  } catch (_) { /* localStorage unavailable; cookie still authoritative */ }
}

function readCachedSession(): AuthUser | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthUser>;
    if (parsed && typeof parsed.retailerId === "string" && typeof parsed.retailerName === "string") {
      const user: AuthUser = {
        retailerId: parsed.retailerId,
        retailerName: parsed.retailerName,
        isPortal: true,
      };
      if (typeof parsed.firstName === "string" && parsed.firstName.trim() !== "") {
        user.firstName = parsed.firstName.trim();
      }
      if (typeof parsed.retailerSlug === "string" && parsed.retailerSlug.trim() !== "") {
        user.retailerSlug = parsed.retailerSlug.trim();
      }
      if (typeof parsed.role === "string" && parsed.role.trim() !== "") {
        user.role = parsed.role.trim();
      }
      if (typeof parsed.userId === "string" && parsed.userId.trim() !== "") {
        user.userId = parsed.userId.trim();
      }
      if (typeof parsed.email === "string" && parsed.email.trim() !== "") {
        user.email = parsed.email.trim();
      }
      // Default-false: a stale cache must never re-enable demo data. The live
      // value is refreshed from /portal/me right after restore.
      user.onboardingComplete = parsed.onboardingComplete === true;
      return user;
    }
    return null;
  } catch (_) { return null; }
}

function clearCachedSession(): void {
  try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch (_) {}
}

// ── Server calls ─────────────────────────────────────────────────────────────

/**
 * Look up the signed-in user's personal profile (first name, etc.) from
 * GET /api/portal/me. The endpoint is cookie-authed; we call it any time
 * we've just confirmed a portal session. Resolves to `null` on any
 * failure — callers should fall back to `retailerName` for display.
 */
async function fetchMe(): Promise<{ firstName: string | null; role: string | null; onboardingComplete: boolean; retailerSlug: string | null; retailerId: string | null; retailerName: string | null; email: string | null } | null> {
  try {
    const resp = await api<MeResponse>(ME_PATH, { method: "GET" });
    const fn = resp?.user?.firstName?.trim();
    const role = resp?.user?.role?.trim();
    const slug = resp?.retailerSlug?.trim();
    const rid = resp?.retailerId?.trim();
    const rname = resp?.retailerName?.trim();
    const email = resp?.user?.email?.trim();
    return {
      firstName: fn ? fn : null,
      role: role ? role : null,
      onboardingComplete: resp?.onboardingComplete === true,
      retailerSlug: slug ? slug : null,
      retailerId: rid ? rid : null,
      retailerName: rname ? rname : null,
      email: email ? email : null,
    };
  } catch (err) {
    if (!(err instanceof ApiError) || err.status >= 500) {
      console.warn("[shell/auth] portal/me lookup failed:", err);
    }
    return null;
  }
}

/**
 * Validate the session by calling `portal/demo-check`. If the cookie is
 * still valid the server returns `{isPortal: true}` and we restore the
 * cached retailer identity from localStorage. Otherwise we treat as
 * anonymous and clear any stale local cache. Refreshes the user's first
 * name from /portal/me so a stale localStorage cache doesn't show wrong
 * personalisation after a real user-row change.
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const resp = await api<CheckResponse>(CHECK_PATH, { method: "GET" });
    if (resp?.isPortal) {
      const cached = readCachedSession();
      setCurrentUser(cached);
      // Best-effort refresh of personal name + onboarding flag in the
      // background. The onboarding flag governs whether demo data may ever
      // render, so we always apply the freshly-fetched value (default false).
      if (cached) {
        void fetchMe().then((me) => {
          if (!me) return;
          const next: AuthUser = { ...cached, onboardingComplete: me.onboardingComplete };
          if (me.firstName) next.firstName = me.firstName;
          if (me.role) next.role = me.role;
          if (me.retailerSlug) next.retailerSlug = me.retailerSlug;
          // Refresh tenant identity from the verified session — overrides any
          // stale localStorage tenant (e.g. a prior demo-login as SJB) so the
          // signed-in retailer's data is always what loads.
          if (me.retailerId) next.retailerId = me.retailerId;
          if (me.retailerName) next.retailerName = me.retailerName;
          if (me.email) next.email = me.email;
          saveCachedSession(next);
          setCurrentUser(next);
        });
      }
      return currentUser;
    }
    clearCachedSession();
    setCurrentUser(null);
    return null;
  } catch (err) {
    if (!(err instanceof ApiError) || err.status >= 500) {
      console.warn("[shell/auth] portal/demo-check failed:", err);
    }
    setCurrentUser(null);
    return null;
  }
}

/**
 * Submit the retailer's email + password to /api/retailer/login. On success
 * the server sets the `kos_portal` cookie (v2, tenant-bound) and returns the
 * retailer record, which we cache locally so the chrome and pages can render
 * retailer-scoped content. The shared-password demo-login route still exists
 * server-side but is no longer used by this UI.
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  const body = JSON.stringify({ email, password });
  const resp = await api<RetailerLoginResponse>(LOGIN_PATH, {
    method: "POST",
    body,
  }).catch((err: unknown) => {
    if (err instanceof ApiError && err.status === 401) {
      throw new Error("Incorrect email or password. Please try again.");
    }
    throw new Error("Connection error. Please try again.");
  });

  if (!resp?.ok || !resp.retailer?.id || !resp.retailer?.name) {
    throw new Error("Login failed. Please try again.");
  }

  const user: AuthUser = {
    // `retailer.id` is the slug; both file-store reads and withRetailerScope
    // resolve a slug, so it is safe to use as the cached retailerId.
    retailerId: resp.retailer.id,
    retailerName: resp.retailer.name,
    retailerSlug: resp.retailer.id,
    isPortal: true,
    // Default-false until /portal/me confirms onboarding — demo data must
    // never render to a real account during the brief pre-/me window.
    onboardingComplete: false,
  };
  if (resp.retailer.email) user.email = resp.retailer.email;
  // Per-user identity from the login response (the session cookie is the
  // authoritative source; this mirrors it for the UI). Role drives role-gating.
  if (resp.user) {
    if (resp.user.id) user.userId = resp.user.id;
    if (resp.user.role) user.role = resp.user.role;
  }
  // Best-effort hydrate personal first name + onboarding flag from
  // /portal/me before we hand back control. Failure is non-fatal — the
  // dashboard falls back to retailerName, and onboardingComplete stays false
  // (empty/zero state) which is the safe default.
  const me = await fetchMe();
  if (me) {
    if (me.firstName) user.firstName = me.firstName;
    if (me.role) user.role = me.role;
    if (me.retailerSlug) user.retailerSlug = me.retailerSlug;
    // Authoritative identity from /portal/me (the login response only carries
    // the slug). retailerId settles on the canonical UUID; retailerName on the
    // retailers.business_name. Keeps boot-restore and login on the same values.
    if (me.retailerId) user.retailerId = me.retailerId;
    if (me.retailerName) user.retailerName = me.retailerName;
    if (me.email) user.email = me.email;
    user.onboardingComplete = me.onboardingComplete;
  }
  saveCachedSession(user);
  setCurrentUser(user);
  return user;
}

/** Sign out. Clears the cookie server-side and the cached user locally. */
export async function logout(): Promise<void> {
  try {
    await api<{ ok: boolean }>(LOGOUT_PATH, { method: "POST" });
  } catch (err) {
    console.warn("[shell/auth] logout call failed:", err);
  }
  clearCachedSession();
  setCurrentUser(null);
}
